import { AGENT_DATA_READY_GRACE_MS, AGENT_DATA_READY_TIMEOUT_MS, awaitAgentDataReady } from '#layers/core/app/runtime/utils/agentDataReady';
import { ToolCallFailure } from '#layers/core/app/runtime/utils/agentToolsRegistry';

// Widok edycji łapie odrzucenie zapytania o encję sam (`handleException` w `catch`) i kończy
// wczytywanie tak samo jak po sukcesie - `ready(...)` albo `continue()` w `finally`. Formularz zostaje
// wtedy w kształcie początkowym (czasem z `id` przepisanym z adresu), a żadna flaga pomocnika nie mówi,
// że dane nie doszły. Narzędzia agenta oddawały więc pustkę jako treść rekordu, a `goto.entity`
// ogłaszało przejście do encji, której nie ma
//
// Sygnał bierzemy z dwóch miejsc, których widok nie przechwytuje: z odpowiedzi HTTP (przechwytywacz
// w `plugins/http.ts` widzi każde odrzucenie, zanim trafi do `catch` widoku) i z samego formularza
// (`withData` zapisuje chwilę wypełnienia). Dzięki temu naprawa działa na każdym widoku formularza bez
// dotykania ani jednego z nich

// Zapytanie uznane za zapytanie o encję otwartej trasy. Odrzucenie trzymamy w całości, bo z niego
// powstaje odmowa z kodem HTTP i komunikatem serwera (`isHttpRejection` w rejestrze)
interface EntityRequest
{
    settled: boolean;
    rejection: unknown;
}

// Pole na konfiguracji zapytania, a nie mapa po obiekcie: przechwytywacz, który zwróciłby kopię
// konfiguracji, zgubiłby wpis w mapie, a pole kopiuje się razem z resztą
const ENTITY_REQUEST = '__agentEntityRequest';

// Zapytania jednego wejścia na trasę i chwila jego początku
interface EntityNavigation
{
    startedAt: number;
    requests: EntityRequest[];
}

// Stan jednego wejścia na trasę. Panel jest aplikacją jednostronicową (`ssr: false`), więc stan
// modułowy należy do jednej karty jednego użytkownika - ten sam powód co przy kontrakcie rejestru
// w `agentTools.ts`
let navigation = 0;
let entityId: string | null = null;
let entityKey: string | null = null;
let current: EntityNavigation = { startedAt: 0, requests: [] };
let inherited: EntityNavigation | undefined;

// Ostatnie wejście, które zapytało o daną encję (nazwa trasy + `id`). Widok trzymany w pamięci
// (`keepalive`) po powrocie z innego widoku na ten sam `id` niczego nie wczytuje ponownie, więc bieżące
// wejście nie ma własnego wyniku, a formularz na ekranie jest wciąż formularzem tamtego wejścia
const lastWithRequests = new Map<string, EntityNavigation>();

// Wołane przy każdej zmianie nazwy trasy albo jej `id`. Zapytania poprzedniego wejścia przestają się
// liczyć - to, że encja 999999 nie istnieje, nie mówi niczego o encji otwartej po niej na tym samym,
// trzymanym w pamięci widoku (`keepalive`)
export function beginAgentEntityNavigation(id: string | null, routeName: string = ''): void
{
    navigation++;
    entityId = id !== null && id.length > 0 ? id : null;
    entityKey = entityId === null ? null : `${routeName}#${entityId}`;
    current = { startedAt: performance.now(), requests: [] };
    inherited = entityKey === null ? undefined : lastWithRequests.get(entityKey);
}

// Zapytaniem o encję jest GET, którego ścieżka ma `id` z adresu jako cały segment. Tylko GET, bo
// odrzucony zapis nie mówi nic o tym, czy rekord się wczytał. Trasa bez `id` (dodawanie, lista) nie ma
// czego śledzić, więc formularz dodawania nigdy nie dostanie tej odmowy
export function trackAgentEntityRequest(config: { method?: string; url?: string }): void
{
    if (entityId === null || (config.method ?? 'get').toLowerCase() !== 'get')
    {
        return;
    }

    const path = (config.url ?? '').split('?')[0];

    if (!path.split('/').includes(entityId))
    {
        return;
    }

    const entry: EntityRequest = { settled: false, rejection: null };

    current.requests.push(entry);
    lastWithRequests.set(entityKey!, current);
    (config as Record<string, unknown>)[ENTITY_REQUEST] = entry;
}

// Zapytanie poprzedniego wejścia rozstrzyga się na swoim wpisie, którego nie ma już w bieżącej
// liście - więc spóźniona odpowiedź z opuszczonej trasy niczego tu nie psuje
export function settleAgentEntityRequest(config: unknown, rejection: unknown): void
{
    const entry = config !== null && typeof config === 'object' ? (config as Record<string, unknown>)[ENTITY_REQUEST] as EntityRequest | undefined : undefined;

    if (entry !== undefined)
    {
        entry.settled = true;
        entry.rejection = rejection;
    }
}

function statusOf(rejection: unknown): unknown
{
    return rejection !== null && typeof rejection === 'object' ? (rejection as { code?: unknown }).code : undefined;
}

// 404 dostaje własny powód, bo to jedyny przypadek, w którym wiadomo na pewno, że rekordu nie ma.
// Każde inne odrzucenie (403, 500, zerwane połączenie) idzie dalej w kształcie panelu i rejestr zamienia
// je na odmowę z kodem HTTP i komunikatem serwera - brak uprawnień nie może wyglądać jak brak rekordu
function entityFailure(rejection: unknown, toolName: string, data: Record<string, unknown> = {}): unknown
{
    if (statusOf(rejection) !== 404)
    {
        return rejection;
    }

    return new ToolCallFailure(
        'entity-not-found',
        `Rekordu o identyfikatorze "${entityId}" nie ma - serwer odpowiedział 404 na zapytanie widoku o tę encję, więc ${toolName} nie ma czego zwrócić. Widok jest otwarty, ale formularz na ekranie ma kształt początkowy, a nie treść rekordu; zapis takiego formularza nie zmieni istniejącej encji. Sprawdź identyfikator na liście.`,
        { ...data, status: 404, id: entityId }
    );
}

// Wejście, którego wynik rozstrzyga: bieżące, chyba że samo nie zapytało o encję i nie wypełniło
// formularza - wtedy ostatnie wejście na tę samą encję. Wynik innego `id` nie przechodzi, bo klucz
// zawiera `id`
function decisive(filledAt: number): EntityNavigation
{
    if (current.requests.length > 0 || filledAt > current.startedAt || inherited === undefined)
    {
        return current;
    }

    return inherited;
}

// Odczyt formularza po wejściu na trasę encji. Czeka, aż zapytanie o encję się rozstrzygnie - bez tego
// widok, który ogłasza koniec wczytywania wcześniej, niż przychodzi odpowiedź o encję (kategorie sklepu
// kończą je obsługą zmiany katalogu), oddałby pustkę przed nadejściem 404
//
// Formularz wypełniony w tym wejściu odpowiada zawsze, nawet przy odrzuconym zapytaniu z `id`
// w ścieżce: wtedy odrzucone było zapytanie poboczne (wersje, historia), a rekord doszedł
export async function assertAgentEntityLoaded(filledAt: () => number, toolName: string): Promise<void>
{
    await awaitAgentDataReady(() =>
    {
        const entered = decisive(filledAt());
        const pending = filledAt() <= entered.startedAt && entered.requests.some(entry => !entry.settled);

        return { loaded: !pending, loading: pending };
    }, 0, toolName);

    const entered = decisive(filledAt());

    if (filledAt() > entered.startedAt)
    {
        return;
    }

    const failed = entered.requests.find(entry => entry.settled && entry.rejection !== null);

    if (failed !== undefined)
    {
        throw entityFailure(failed.rejection, toolName);
    }
}

// Wynik `goto.entity`. Przejście kończy się, zanim widok w ogóle zapyta o encję, więc samo przejście nie
// mówi, czy rekord istnieje. Czekamy na pierwsze zapytanie o encję nowego wejścia i bierzemy jego wynik:
// widok pyta najpierw o samą encję, a dopiero po niej o dane poboczne. Narzędzie nie widzi formularza
// widoku, więc nie ma tu sygnału wypełnienia - pierwsze zapytanie jest najlepszym, jaki jest
//
// Gdy przejście nie zaczęło nowego wejścia (ta sama encja jeszcze raz), rozstrzyga to, co już wiadomo
// o bieżącym. Gdy widok w karencji nie zapyta o nic z `id` w ścieżce, rozstrzyga ostatnie wejście na tę
// samą encję (widok z pamięci po powrocie), a bez niego odpowiadamy jak dotąd - ten widok pobiera encję
// inaczej i nie mamy o niej czego powiedzieć
//
// Werdykt wyłącznie dla encji, o którą proszono: przejście przerwane przez strażnika trasy zostawia
// otwarte poprzednie wejście, a jego 404 nie jest odpowiedzią o tę encję
export async function awaitAgentEntityOutcome(before: number, id: string, toolName: string, data: Record<string, unknown>): Promise<void>
{
    if (entityId !== id)
    {
        return;
    }

    const graceUntil = navigation === before ? 0 : Date.now() + AGENT_DATA_READY_GRACE_MS;

    try
    {
        await awaitAgentDataReady(() =>
        {
            const first = current.requests[0];

            return { loaded: first !== undefined && first.settled, loading: first !== undefined && !first.settled };
        }, graceUntil, toolName, AGENT_DATA_READY_TIMEOUT_MS);
    }
    catch
    {
        // Encja, która nie odpowiedziała w granicy czekania, nie jest dowodem, że jej nie ma - przejście
        // się odbyło, a o treść rekordu rozstrzygnie odczyt formularza, który ma własne czekanie
        return;
    }

    const first = (current.requests.length > 0 ? current : inherited ?? current).requests[0];

    if (first !== undefined && first.rejection !== null)
    {
        throw entityFailure(first.rejection, toolName, data);
    }
}

// Numer wejścia sprzed przejścia - po nim `awaitAgentEntityOutcome` poznaje, że przejście zaczęło nowe
export function agentEntityNavigation(): number
{
    return navigation;
}
